import React, { useEffect, useState } from 'react';
import { Radar, Lock, Wifi, ArrowRight, CreditCard, Mail } from 'lucide-react';
import { api, API } from '@/lib/api';
import { APP } from '@/constants/testIds';
import { useAuth } from '@/context/AuthContext';
import { toast } from 'sonner';

const riskClass = { low: 'pill-low', medium: 'pill-medium', high: 'pill-high', critical: 'pill-critical', locked: 'pill-muted' };

const NetworkScan = () => {
  const { user, refresh } = useAuth();
  const isPaid = user?.payment_status === 'paid' || user?.role === 'admin';
  const credits = Number(user?.credits ?? 0);
  const scansAvailable = Math.floor(credits / 100);
  const canScan = isPaid || credits >= 100;
  const [networks, setNetworks] = useState([]);
  const [selected, setSelected] = useState('');
  const [newName, setNewName] = useState('Home Network');
  const [location, setLocation] = useState('Primary');
  const [consent, setConsent] = useState(false);
  const [scanning, setScanning] = useState(false);
  const [progress, setProgress] = useState(0);
  const [result, setResult] = useState(null);
  const [logs, setLogs] = useState([]);
  const [stage, setStage] = useState('Idle');

  useEffect(() => {
    api.get('/networks').then((r) => {
      setNetworks(r.data);
      if (r.data[0]) setSelected(r.data[0].network_id);
    });
  }, []);

  const startScan = async () => {
    if (!consent) { toast.error('You must confirm consent to scan.'); return; }
    setScanning(true);
    setResult(null);
    setProgress(0);
    setLogs([]);
    setStage('Initializing scanner');
    const lines = [
      '[*] Booting tactical scanner v4.2',
      '[*] Loading vendor fingerprint DB · 87,402 OUI entries',
      '[+] ARP probe broadcast on 192.168.1.0/24',
      '[+] ICMP echo sweep · TTL 64',
      '[+] mDNS multicast discovery active',
      '[+] SSDP / UPnP enumeration',
      '[+] NetBIOS name resolution',
      '[+] Resolving MAC vendors via OUI',
      '[+] Cross-referencing trusted device list',
      '[*] Probing TCP ports · top 1000',
      '[*] Fingerprinting service banners',
      '[*] Computing risk score · CVSS heuristics',
      '[!] Threat correlation against known IOCs',
    ];
    const stages = [
      [0, 'Initializing scanner'],
      [15, 'Network discovery'],
      [35, 'Port enumeration'],
      [55, 'Service fingerprint'],
      [75, 'Threat analysis'],
      [95, 'Generating report'],
    ];
    let i = 0;
    const t = setInterval(() => {
      setLogs((p) => [...p, lines[i % lines.length] + ` (0x${(0xC0A801 + i).toString(16).toUpperCase()})`]);
      setProgress((p) => {
        const next = Math.min(p + (isPaid ? 7 : 5), 92);
        const stage = stages.findLast(([t]) => next >= t);
        if (stage) setStage(stage[1]);
        return next;
      });
      i += 1;
    }, 280);

    try {
      const body = selected
        ? { network_id: selected, consent: true }
        : { consent: true, network_name: newName, location };
      const { data } = await api.post('/scans/start', body);
      setTimeout(() => {
        clearInterval(t);
        setProgress(100);
        setStage('Complete');
        setResult(data);
        setScanning(false);
        refresh?.();
        if (data.locked) {
          toast.error('Scan complete · results locked. Add credits or upgrade to reveal.');
        } else if (data.credits_used) {
          toast.success(`Scan complete — risk ${(data.risk_level || 'low').toUpperCase()} · ${data.credits_used} credits used (${data.credits_remaining} remaining)`);
        } else {
          toast.success(`Scan complete — risk level ${(data.risk_level || 'low').toUpperCase()}`);
        }
        // Low-balance warning
        const remaining = data.credits_remaining;
        if (typeof remaining === 'number' && remaining > 0 && remaining < 200) {
          const left = Math.floor(remaining / 100);
          toast.warning(`Heads up — only ${left} scan${left === 1 ? '' : 's'} left (${remaining} credits).`, {
            duration: 6000,
            description: 'Add credits or upgrade to keep scanning.',
          });
        }
      }, 2400);
    } catch (e) {
      clearInterval(t);
      setScanning(false);
      toast.error(e?.response?.data?.detail || 'Scan failed');
    }
  };

  const loadScanDevices = async (scanId) => {
    const { data } = await api.get(`/scans/${scanId}`);
    setResult(data);
  };

  return (
    <div className="space-y-10">
      <header>
        <div className="label-tag mb-2">// Network Scan</div>
        <h1 className="text-4xl md:text-5xl font-light tracking-tight">Probe your network</h1>
        <p className="text-[color:var(--text-secondary)] mt-2 max-w-2xl">
          Simulated, consent-gated network discovery. We fingerprint devices, flag unknowns, and surface old or suspicious IPs.
        </p>
      </header>

      {!isPaid && (
        <section
          data-testid="payment-required-banner"
          className={`panel p-5 md:p-6 ${canScan ? 'border-[color:var(--brand)] bg-[rgba(0,240,255,0.04)]' : 'border-[color:var(--status-critical)] bg-[rgba(255,42,42,0.06)]'}`}
        >
          <div className="flex flex-col md:flex-row items-start gap-4 md:gap-5">
            <div className={`w-10 h-10 md:w-12 md:h-12 border flex items-center justify-center shrink-0 ${canScan ? 'border-[color:var(--brand)]' : 'border-[color:var(--status-critical)]'}`}>
              {canScan ? <CreditCard size={18} className="text-[color:var(--brand)]" /> : <Lock size={18} className="text-[color:var(--status-critical)]" />}
            </div>
            <div className="flex-1">
              <div className={`label-tag mb-1 ${canScan ? 'text-[color:var(--brand)]' : 'text-[color:var(--status-critical)]'}`}>
                {canScan ? '// Trial Credits Active' : '// Credits Depleted'}
              </div>
              <h2 className="text-xl md:text-2xl font-light tracking-tight mb-2">
                {canScan
                  ? `${credits.toLocaleString()} credits available · ${scansAvailable} ${scansAvailable === 1 ? 'scan' : 'scans'} remaining`
                  : 'Credits depleted. Upgrade or ask an admin to top up to continue.'}
              </h2>
              <p className="text-[color:var(--text-secondary)] leading-relaxed mb-4 max-w-3xl text-sm md:text-base">
                Hi {user?.name?.split(' ')[0] || 'there'} — each network scan costs <span className="text-[color:var(--brand)] font-mono">100 credits</span>. While credits remain you have full access:
                unmasked IPs &amp; MACs, complete device inventory, and risk-scored results. To unlock unlimited access (and PDF/CSV/Email reports),
                contact billing for an admin to mark your account as <span className="text-[color:var(--status-low)] font-mono">PAID</span>.
              </p>
              <div className="flex flex-wrap gap-2">
                <a
                  href="mailto:billing@supremeweb.example?subject=Add%20Credits%20-%20Supreme%20Web%20Security"
                  data-testid="payment-contact-billing-btn"
                  className="btn-primary"
                >
                  <Mail size={14} /> Request More Credits
                </a>
                <a href="/app/support" data-testid="payment-open-ticket-btn" className="btn-secondary">
                  Open Support Ticket <ArrowRight size={14} />
                </a>
              </div>
            </div>
          </div>
        </section>
      )}

      <section className="grid grid-cols-1 lg:grid-cols-3 gap-px bg-[color:var(--border)]">
        <div className="bg-[color:var(--bg-base)] p-6 lg:col-span-2">
          <div className="label-tag mb-3">// Scan Configuration</div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-5 mb-6">
            <div>
              <div className="label-tag mb-2">Target Network</div>
              {networks.length > 0 ? (
                <select value={selected} onChange={(e) => setSelected(e.target.value)} className="input-base" data-testid="scan-network-select">
                  <option value="">+ Create new</option>
                  {networks.map((n) => (
                    <option key={n.network_id} value={n.network_id}>{n.name} — {n.cidr}</option>
                  ))}
                </select>
              ) : (
                <input
                  data-testid={APP.scanNetworkInput}
                  className="input-base"
                  value={newName}
                  onChange={(e) => setNewName(e.target.value)}
                  placeholder="Home Network"
                />
              )}
            </div>
            <div>
              <div className="label-tag mb-2">Location</div>
              <input
                data-testid={APP.scanLocationInput}
                className="input-base"
                value={location}
                onChange={(e) => setLocation(e.target.value)}
                placeholder="Primary"
                disabled={!!selected}
              />
            </div>
          </div>

          <label className="flex items-start gap-3 panel-soft p-4 cursor-pointer hover:border-[color:var(--brand)] transition-colors">
            <input
              data-testid={APP.scanConsent}
              type="checkbox"
              checked={consent}
              onChange={(e) => setConsent(e.target.checked)}
              className="mt-1"
            />
            <div>
              <div className="flex items-center gap-2 mb-1">
                <Lock size={13} className="text-[color:var(--brand)]" />
                <span className="font-mono text-[11px] uppercase tracking-widest">Authorization Required</span>
              </div>
              <p className="text-sm text-[color:var(--text-secondary)] leading-relaxed">
                I confirm that I own or have authorization to scan this network and request Supreme Web Security to perform a security review.
              </p>
            </div>
          </label>

          <div className="mt-6 flex items-center gap-3">
            <button
              data-testid={APP.scanStartBtn}
              disabled={scanning || !consent}
              onClick={startScan}
              className="btn-primary disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <Radar size={14} /> {scanning ? 'Scanning…' : 'Start Security Scan'}
            </button>
            {scanning && <span className="font-mono text-[11px] uppercase tracking-widest text-[color:var(--brand)] blink">// Live probe in progress</span>}
          </div>
        </div>

        <div className="bg-[color:var(--bg-base)] p-6 relative overflow-hidden flex flex-col items-center justify-center min-h-[320px]">
          <div className="label-tag mb-4 self-start">// Tactical Scanner</div>

          {/* Radial progress */}
          <div className="relative w-44 h-44 sm:w-52 sm:h-52">
            <svg viewBox="0 0 200 200" className="w-full h-full -rotate-90">
              <circle cx="100" cy="100" r="86" fill="none" stroke="#141414" strokeWidth="6" />
              <circle
                cx="100" cy="100" r="86"
                fill="none"
                stroke={progress >= 100 && result?.locked ? '#ffb302' : 'var(--brand)'}
                strokeWidth="6"
                strokeLinecap="square"
                strokeDasharray={`${(progress / 100) * 540} 540`}
                style={{ transition: 'stroke-dasharray 0.4s ease, stroke 0.3s ease', filter: scanning ? 'drop-shadow(0 0 8px var(--brand))' : 'none' }}
              />
              {scanning && (
                <circle cx="100" cy="100" r="68" fill="none" stroke="rgba(0,240,255,0.35)" strokeWidth="1" strokeDasharray="4 6">
                  <animateTransform attributeName="transform" type="rotate" from="0 100 100" to="360 100 100" dur="3s" repeatCount="indefinite" />
                </circle>
              )}
            </svg>
            <div className="absolute inset-0 flex flex-col items-center justify-center">
              <div className="font-mono text-[10px] uppercase tracking-[0.22em] text-[color:var(--text-muted)]">{stage}</div>
              <div className="text-4xl sm:text-5xl font-light text-[color:var(--brand)] tabular-nums">{progress}%</div>
              <div className="font-mono text-[10px] uppercase tracking-[0.22em] text-[color:var(--text-secondary)] mt-1">
                {scanning ? 'analyzing' : (result ? 'complete' : 'idle')}
              </div>
            </div>
          </div>

          <div className="relative panel-soft h-32 sm:h-36 overflow-hidden w-full mt-5">
            {scanning && <div className="scan-overlay" />}
            <pre className="absolute inset-0 p-3 text-[10.5px] leading-5 font-mono text-[color:var(--text-secondary)] overflow-y-auto whitespace-pre-wrap">
{logs.length === 0 ? 'idle$ awaiting consent...' : logs.slice(-12).join('\n')}
            </pre>
          </div>
        </div>
      </section>

      {result && (
        <section data-testid={APP.scanResults} className="space-y-6 relative">
          {result.locked && (
            <div className="panel p-6 sm:p-8 border-[color:var(--status-medium)] bg-[rgba(255,179,2,0.06)]">
              <div className="flex flex-col md:flex-row items-start gap-4">
                <div className="w-12 h-12 border border-[color:var(--status-medium)] flex items-center justify-center shrink-0">
                  <Lock size={20} className="text-[color:var(--status-medium)]" />
                </div>
                <div className="flex-1">
                  <div className="label-tag text-[color:var(--status-medium)] mb-2">// Results Locked · Upgrade Required</div>
                  <h2 className="text-2xl sm:text-3xl font-light tracking-tight mb-3">
                    Scan completed. Results are protected.
                  </h2>
                  <p className="text-[color:var(--text-secondary)] leading-relaxed mb-4 text-sm md:text-base">
                    Our tactical engine just finished a full sweep of <span className="text-[color:var(--brand)]">{result.network_name}</span>.
                    Device count, new/unknown/suspicious tallies, IP/MAC addresses, risk score and the downloadable PDF + CSV are sealed behind your upgrade.
                    A Supreme Web Security admin will unlock everything the moment payment is confirmed.
                  </p>
                  <div className="flex flex-wrap gap-2">
                    <a href="mailto:billing@supremeweb.example?subject=Unlock%20Scan%20Results" className="btn-primary">
                      <Mail size={14} /> Confirm Payment
                    </a>
                    <a href="/app/support" className="btn-secondary">
                      Open Ticket <ArrowRight size={14} />
                    </a>
                  </div>
                </div>
              </div>
            </div>
          )}
          <div className="grid grid-cols-2 md:grid-cols-5 gap-px bg-[color:var(--border)] relative">
            {[
              ['Devices', result.devices_found],
              ['New', result.new_devices],
              ['Unknown', result.unknown_devices],
              ['Old IPs', result.old_ips],
              ['Suspicious', result.suspicious],
            ].map(([k, v]) => (
              <div key={k} className="bg-[color:var(--bg-base)] p-5">
                <div className="label-tag mb-2">{k}</div>
                <div className={`text-3xl font-light ${result.locked ? 'text-[color:var(--text-muted)]' : ''}`}>
                  {result.locked ? '••' : (v ?? 0)}
                </div>
              </div>
            ))}
          </div>
          {!result.locked && (
            <div className="panel p-6">
              <div className="flex flex-wrap items-center justify-between gap-4 mb-5">
                <div>
                  <div className="label-tag">// Scan Result</div>
                  <h2 className="text-2xl font-light mt-1">{result.network_name}</h2>
                </div>
                <div className="flex items-center gap-3">
                  <span data-testid={APP.scanRiskBadge} className={`pill ${riskClass[result.risk_level]}`}>
                    Risk: {result.risk_level}
                  </span>
                  <a className="btn-secondary" target="_blank" rel="noreferrer" href={`${API}/scans/${result.scan_id}/pdf`}>
                    Download PDF <ArrowRight size={12} />
                  </a>
                </div>
              </div>
              {!result.devices && (
                <button className="btn-secondary mb-4" onClick={() => loadScanDevices(result.scan_id)}>
                  <Wifi size={12} /> Load Device Detail
                </button>
              )}
              {result.devices && (
                <div className="overflow-x-auto">
                  <table className="dtable min-w-[640px]">
                    <thead><tr><th>IP</th><th>MAC</th><th>Device</th><th>Vendor</th><th>Conn</th><th>Status</th></tr></thead>
                    <tbody>
                      {result.devices.map((d) => (
                        <tr key={d.device_id}>
                          <td>{d.ip}</td>
                          <td>{d.mac}</td>
                          <td>{d.custom_name || d.name}</td>
                          <td>{d.vendor}</td>
                          <td className="uppercase text-[color:var(--text-secondary)]">{d.connection}</td>
                          <td>
                            <span className={`pill ${
                              d.status === 'suspicious' ? 'pill-critical' :
                          d.status === 'new' ? 'pill-medium' :
                          d.status === 'old' ? 'pill-high' :
                          d.status === 'blocked' ? 'pill-critical' : 'pill-low'
                        }`}>{d.status}</span>
                      </td>
                    </tr>
                  ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          )}
        </section>
      )}
    </div>
  );
};

export default NetworkScan;
